import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Headphones, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ulid } from "ulid";
import { useStore } from "zustand";
import { ConnectionWaitScreen } from "@/components/auth/connection-wait";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { useConnectionWait } from "@/hooks/use-connection-wait";
import { useSendToSignIn } from "@/hooks/use-send-to-sign-in";
import { claimSocket } from "@/lib/socket-owner";
import { authStore } from "@/lib/stores/auth-store";
import { ws } from "@/lib/websocket-wrapper";

type InviteInfo = {
	valid: boolean;
	error?: string;
	channel?: {
		id: string;
		name: string;
		type: string;
		workspaceId: string | null;
		workspaceName?: string;
	};
};

export const Route = createFileRoute("/invite/$code")({
	component: InviteComponent,
});

function InviteComponent() {
	const { code } = Route.useParams();
	const navigate = useNavigate();
	const user = useStore(authStore, (state) => state.user);
	const initialized = useStore(authStore, (state) => state.initialized);
	const wsConnected = useStore(authStore, (state) => state.wsConnected);
	const wait = useConnectionWait(wsConnected);

	const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
	const [loading, setLoading] = useState(true);
	const [joining, setJoining] = useState(false);

	// Invites are an `/app` session concern, so make sure that session owns the
	// socket.
	useEffect(() => {
		claimSocket("app").catch((error) => {
			console.error("Failed to claim the socket for /app:", error);
		});
	}, []);

	// The invite is the destination: sign-in lands the user back here.
	useSendToSignIn();

	useEffect(() => {
		if (!(wsConnected && user)) return;

		const validateInvite = async () => {
			try {
				const requestId = ulid();
				const response = await ws.sendWithPromise<InviteInfo>({
					id: requestId,
					procedure: "invite.validate",
					data: { code },
				});

				setInviteInfo(response);
				if (!response.valid && response.error) {
					toast.error(response.error);
				}
			} catch (error) {
				console.error("Failed to validate invite:", error);
				setInviteInfo({ valid: false, error: "Failed to validate invite" });
			} finally {
				setLoading(false);
			}
		};

		validateInvite();
	}, [code, wsConnected, user]);

	const handleJoin = async () => {
		if (!inviteInfo?.channel) return;

		setJoining(true);
		try {
			const requestId = ulid();
			await ws.sendWithPromise({
				id: requestId,
				procedure: "invite.use",
				data: { code },
			});

			sessionStorage.setItem(
				"pendingVoiceChannel",
				JSON.stringify({
					id: inviteInfo.channel.id,
					name: inviteInfo.channel.name,
					type: "voice_and_text",
					workspaceId: inviteInfo.channel.workspaceId,
				})
			);

			toast.success(`Joined ${inviteInfo.channel.name}`);
			navigate({ to: "/app" });
		} catch (error) {
			console.error("Failed to join channel:", error);
			const errorMessage =
				error instanceof Error ? error.message : "Failed to join channel";
			toast.error(errorMessage);
		} finally {
			setJoining(false);
		}
	};

	if (!(initialized && user)) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
			</div>
		);
	}

	// The invite is checked over the socket, so until it opens the wait is the
	// connection's, and says so.
	if (loading && !wsConnected) {
		return <ConnectionWaitScreen className="min-h-screen" wait={wait} />;
	}

	if (loading) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<div className="flex flex-col items-center gap-4">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
					<p className="text-muted-foreground">Validating invite...</p>
				</div>
			</div>
		);
	}

	if (!inviteInfo?.valid) {
		return (
			<div className="flex min-h-screen items-center justify-center p-4">
				<Card className="w-full max-w-md">
					<CardHeader>
						<CardTitle>Invalid Invite</CardTitle>
						<CardDescription>
							{inviteInfo?.error || "This invite link is not valid."}
						</CardDescription>
					</CardHeader>
					<CardFooter>
						<Button
							className="w-full"
							onClick={() => navigate({ to: "/app" })}
							variant="outline"
						>
							Go to App
						</Button>
					</CardFooter>
				</Card>
			</div>
		);
	}

	return (
		<div className="flex min-h-screen items-center justify-center p-4">
			<Card className="w-full max-w-md">
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Headphones className="h-5 w-5" />
						Voice Channel Invite
					</CardTitle>
					<CardDescription>
						You've been invited to join {inviteInfo.channel?.name}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="rounded-lg border bg-muted/50 p-4">
						<p className="font-medium">{inviteInfo.channel?.name}</p>
						{inviteInfo.channel?.workspaceName && (
							<p className="text-muted-foreground text-sm">
								in {inviteInfo.channel.workspaceName}
							</p>
						)}
					</div>
				</CardContent>
				<CardFooter>
					<Button className="w-full" disabled={joining} onClick={handleJoin}>
						{joining ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Joining...
							</>
						) : (
							"Join Voice Channel"
						)}
					</Button>
				</CardFooter>
			</Card>
		</div>
	);
}
